import { z } from "zod";
import { CONFLICT_MSG } from "./concurrent.js";
import { OrderRef, VendorQuoteAdoption } from "./orderBundle.js";

// ---------------------------------------------------------------------------
// 外注見積の一覧（#198、docs/79、2026-10-17 射場さんの決定）
//   案件をまたいで{vendorQuote}を見る一覧。左のメニュー「見積書」の下（使う機能 vendorQuote の会社だけ）、案件の画面のタブは同じ一覧を案件で絞って開く。
//   状態は5つ（AI読み取り中／要確認／未反映／反映済み（一部なら「一部反映」）／不採用）。判定は DB のビュー v_vendor_quote_list 1か所
//   （docs/79 §11.1 ナ）で、ここは言葉（チップの名前・理由の文）と問い合わせ・答えの形だけ。
//   一覧の金額は税抜、税込は右側パネル。表示切替は 一覧／{vendor}別／{project}別。
//   画面の言葉は {vendorQuote}・{vendor}・{project}・{subject} と書いて t() を通す（現場力＝外注見積・外注先・案件・工事名／営業力＝仕入先見積・仕入先・案件・件名）
// ---------------------------------------------------------------------------

const Yen = z.number().int().min(-999_999_999_999).max(999_999_999_999);
const Uuid = z.string().uuid();
const Ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** 一覧の状態（v_vendor_quote_list.state）。used は partial（一部反映）を含む */
export const VendorQuoteListState = z.enum(["reading", "review", "unused", "used", "rejected"]);
export type VendorQuoteListState = z.infer<typeof VendorQuoteListState>;

/** 状態のタブ（すべて＋5つ）。並びも画面のとおり */
export const VENDOR_QUOTE_LIST_TABS = ["all", "reading", "review", "unused", "used", "rejected"] as const;
export type VendorQuoteListTab = (typeof VENDOR_QUOTE_LIST_TABS)[number];

/** タブ・チップの名前。AI読み取り中は 10/11 の決め（画面で AI と名乗る）と今のチップ VENDOR_QUOTE_STATUS_LABEL.reading と同じ言葉（docs/79 §2 イ） */
export const VENDOR_QUOTE_LIST_STATE_LABEL: Record<VendorQuoteListTab, string> = {
  all: "すべて",
  reading: "AI読み取り中",
  review: "要確認",
  unused: "未反映",
  used: "反映済み",
  rejected: "不採用",
};
/** 反映済みのうち一部だけ使ったもののチップ（タブは「反映済み」にまとめる） */
export const VENDOR_QUOTE_PARTIAL_LABEL = "一部反映";

/** 要確認の理由（v_vendor_quote_list.reasons。並びも DB と同じ） */
export const VendorQuoteReviewReason = z.enum(["failed", "no_lines", "check_mismatch", "no_vendor", "no_group"]);
export type VendorQuoteReviewReason = z.infer<typeof VendorQuoteReviewReason>;

/**
 * 要確認の理由を文に（{vendor}・{project} は画面が t() で埋める）。外注先と案件の未設定は1つの文にまとめる。
 * checkNote＝検算の不一致の式（「明細の和 977,000 ≠ 合計 980,000」）
 */
export function vendorQuoteReasonTexts(reasons: readonly VendorQuoteReviewReason[], checkNote?: string | null): string[] {
  const out: string[] = [];
  if (reasons.includes("failed")) out.push("読み取れませんでした");
  if (reasons.includes("no_lines")) out.push("行が未入力");
  if (reasons.includes("check_mismatch")) out.push(checkNote ? `検算 不一致：${checkNote}` : "検算 不一致");
  const miss = [reasons.includes("no_vendor") ? "{vendor}" : null, reasons.includes("no_group") ? "{project}" : null].filter((x): x is string => !!x);
  if (miss.length) out.push(`${miss.join("・")}が未設定`);
  return out;
}

/** 状態のチップの名前（一部反映を見分ける） */
export const vendorQuoteStateLabel = (state: VendorQuoteListState, partial: boolean): string =>
  state === "used" && partial ? VENDOR_QUOTE_PARTIAL_LABEL : VENDOR_QUOTE_LIST_STATE_LABEL[state];

export const VendorQuoteListView = z.enum(["list", "vendor", "group"]);
export type VendorQuoteListView = z.infer<typeof VendorQuoteListView>;
export const VendorQuoteListSort = z.enum(["received_desc", "received_asc", "amount_desc", "amount_asc"]);
export type VendorQuoteListSort = z.infer<typeof VendorQuoteListSort>;
/** 期間（取込日）：すべて（既定。docs/79 §9 の7）／今月／先月／過去3か月／期間を指定 */
export const VendorQuoteListPeriod = z.enum(["all", "this", "last", "3m", "range"]);
export type VendorQuoteListPeriod = z.infer<typeof VendorQuoteListPeriod>;

/** 問い合わせの数（空の文字は「指定なし」） */
const OptYen = z.preprocess((v) => (v === "" || v == null ? undefined : v), z.coerce.number().int().min(0).max(999_999_999_999).optional());

/** GET /vendor-quotes の問い合わせ（URL の問い合わせにもそのまま持つ） */
export const VendorQuoteListQuery = z.object({
  view: VendorQuoteListView.default("list"),
  period: VendorQuoteListPeriod.default("all"),
  from: Ymd.optional(),
  to: Ymd.optional(),
  /** {vendor}の id か none（未設定） */
  vendor: z.union([z.literal("none"), Uuid]).optional(),
  /** {project}の id か none（未設定） */
  group: z.union([z.literal("none"), Uuid]).optional(),
  state: VendorQuoteListState.optional(),
  /** 担当者の id・me（自分）・none（未設定） */
  assignee: z.union([z.literal("me"), z.literal("none"), Uuid]).optional(),
  /** 金額（税抜）の範囲。入れると金額の無い外注見積は出ない */
  min: OptYen,
  max: OptYen,
  /** 件名・番号の部分一致 */
  q: z.string().trim().max(100).optional(),
  /** 採用・不採用で絞る（#233 2a） */
  adoption: VendorQuoteAdoption.optional(),
  sort: VendorQuoteListSort.default("received_desc"),
  cursor: z.string().max(300).optional(),
  /** 0 なら件数・合計・小計だけ（案件のタブの件数など） */
  limit: z.coerce.number().int().min(0).max(200).default(50),
});
export type VendorQuoteListQuery = z.infer<typeof VendorQuoteListQuery>;

/** 資料の種類（一覧の「資料」のアイコン）：PDF／画像／FAX（登録のときの「元」が fax） */
export const VendorQuoteDocKind = z.enum(["pdf", "image", "fax"]);
export type VendorQuoteDocKind = z.infer<typeof VendorQuoteDocKind>;
export const VENDOR_QUOTE_DOC_KIND_LABEL: Record<VendorQuoteDocKind, string> = { pdf: "PDF", image: "画像", fax: "FAX" };

/** 一覧の1行（右側パネルの頭も同じ形） */
export const VendorQuoteRow = z.object({
  id: Uuid,
  receivedAt: z.string(),
  /** {vendor}（略称）。null＝未設定 */
  vendor: z.object({ id: Uuid, name: z.string() }).nullable(),
  /** AI が読んだ発行元の名前（{vendor}が未設定のときの候補） */
  issuerName: z.string().nullable(),
  group: z.object({ id: Uuid, name: z.string() }).nullable(),
  /** 件名（vendor_quotes.title）。空なら "" */
  title: z.string(),
  /** 原本のファイル名（件名が空のときに出す） */
  fileName: z.string().nullable(),
  quoteNo: z.string(),
  quotedOn: z.string().nullable(),
  totalExTax: Yen.nullable(),
  taxAmount: Yen.nullable(),
  totalIncTax: Yen.nullable(),
  /** 読み取りの列：ok＝検算一致／mismatch＝不一致（checkAcked なら確認済み）／failed＝読み取れず／unknown＝—（AI読み取り中・手入力） */
  check: z.enum(["ok", "mismatch", "unknown", "failed"]),
  checkAcked: z.boolean(),
  checkNote: z.string().nullable(),
  state: VendorQuoteListState,
  partial: z.boolean(),
  /** AI読み取り中のうち、まだ処理役が取りに来ていない（順番待ち） */
  waiting: z.boolean(),
  reasons: z.array(VendorQuoteReviewReason),
  /** 反映：自社見積に使った行数／全行数・反映先の見積 */
  usage: z.object({
    done: z.number().int(),
    all: z.number().int(),
    documents: z.array(z.object({ id: Uuid, displayNo: z.string().nullable(), subject: z.string() })),
  }),
  source: VendorQuoteDocKind,
  pageCount: z.number().int().nullable(),
  assignee: z.object({ id: Uuid, name: z.string() }).nullable(),
  registeredBy: z.object({ id: Uuid, name: z.string() }).nullable(),
  /** 見積依頼の回答なら、その依頼（番号は RQ-0003 の形） */
  rfq: z.object({ id: Uuid, displayNo: z.string().nullable(), status: z.string() }).nullable(),
  rejected: z.object({ at: z.string(), reason: z.string().nullable() }).nullable(),
  /** 採用／不採用（#233 2a。null＝未選択）。不採用は rejected と同じもの */
  adoption: VendorQuoteAdoption.nullable().default(null),
  /** この回答から作った{order}（取消を除く・下書きも）。2枚目の確かめ */
  orders: z.array(OrderRef).default([]),
  /** 版（PUT /vendor-quotes/:id の version に送り返す） */
  updatedAt: z.string(),
});
export type VendorQuoteRow = z.infer<typeof VendorQuoteRow>;

const StateCounts = z.object({
  all: z.number().int(),
  reading: z.number().int(),
  review: z.number().int(),
  unused: z.number().int(),
  used: z.number().int(),
  rejected: z.number().int(),
});
export type VendorQuoteStateCounts = z.infer<typeof StateCounts>;

/** 外注先別・案件別の小計行 */
export const VendorQuoteListGroup = z.object({
  /** {vendor}・{project}の id か none（未設定） */
  key: z.string(),
  name: z.string(),
  count: z.number().int(),
  totalExTax: Yen,
  counts: StateCounts,
  latestAt: z.string(),
});
export type VendorQuoteListGroup = z.infer<typeof VendorQuoteListGroup>;

export const VendorQuoteListResponse = z.object({
  items: z.array(VendorQuoteRow),
  /** 状態のタブの件数（状態の外の絞り込みを当てた数） */
  counts: StateCounts,
  /** 下の段：件数・税抜の和・金額の無い件数（状態も当てた数） */
  total: z.object({ count: z.number().int(), exTax: Yen, noAmount: z.number().int() }),
  /** view が vendor／group のときだけ */
  groups: z.array(VendorQuoteListGroup).nullable(),
  nextCursor: z.string().nullable(),
  /** 当月の読み取りの通数と上限（null＝受付開始前で制限なし） */
  quota: z.object({ used: z.number().int(), limit: z.number().int().nullable() }),
});
export type VendorQuoteListResponse = z.infer<typeof VendorQuoteListResponse>;

/** GET /vendor-quotes/filters：絞り込みの選ぶ欄の中身（外注見積のある{vendor}・{project}だけ） */
export const VendorQuoteListFilters = z.object({
  vendors: z.array(z.object({ id: Uuid, name: z.string() })),
  groups: z.array(z.object({ id: Uuid, name: z.string() })),
  users: z.array(z.object({ id: Uuid, name: z.string() })),
});
export type VendorQuoteListFilters = z.infer<typeof VendorQuoteListFilters>;

/** 不採用の理由の候補（任意。docs/79 §2 ヘ） */
export const VENDOR_QUOTE_REJECT_REASONS = ["他社に決定", "金額が合わない", "{project}の中止", "その他"] as const;
export const VendorQuoteRejectBody = z.object({ reason: z.string().trim().max(200).nullable().optional() });
export type VendorQuoteRejectBody = z.infer<typeof VendorQuoteRejectBody>;

/** PUT /vendor-quotes/:id/rfq：見積依頼との紐づけ（手で）。null で外す */
export const VendorQuoteRfqLink = z.object({ rfqId: Uuid.nullable() });
export type VendorQuoteRfqLink = z.infer<typeof VendorQuoteRfqLink>;

/** 回答待ちの見積依頼の候補（{vendor}が同じ・{project}が同じなら先） */
export const VendorQuoteRfqCandidate = z.object({ id: Uuid, displayNo: z.string().nullable(), subject: z.string(), groupName: z.string().nullable() });
export type VendorQuoteRfqCandidate = z.infer<typeof VendorQuoteRfqCandidate>;

/** CSV の列（付録A）。{vendor}・{project} は API が会社の言葉で埋める。自社見積の売価・仕入れは入れない */
export const VENDOR_QUOTE_LIST_CSV_HEADERS = [
  "取込日", "{vendor}", "{vendor}（読み取り）", "{project}", "件名", "見積番号", "見積日", "金額（税抜）", "消費税", "金額（税込）",
  "検算", "状態", "反映した行", "全行", "反映先の見積", "依頼番号", "担当者", "資料", "不採用の理由",
] as const;
/** CSV は 1回 5,000行まで */
export const VENDOR_QUOTE_LIST_CSV_MAX = 5000;

/**
 * 詳細の画面（docs/79 §13）：原本のページの画像（決定 C。受け取った書類と同じ作り＝初めて開いたときに作る・長辺 1600px の JPEG）。
 * url は短命の署名つき（15〜30分）。作れなかったときは空（画面は「別窓で原本表示」だけ）
 */
export const VendorQuotePage = z.object({ n: z.number().int().positive(), url: z.string(), width: z.number().int().nullable(), height: z.number().int().nullable() });
export type VendorQuotePage = z.infer<typeof VendorQuotePage>;
export const VendorQuotePages = z.object({ id: Uuid, pages: z.array(VendorQuotePage) });
export type VendorQuotePages = z.infer<typeof VendorQuotePages>;
/** 前へ／次へ（決定 B）：一覧の条件と並びのまま隣の外注見積。index は 1 から（条件に合わない＝一覧に居ないときは null） */
export const VendorQuoteNeighbors = z.object({ prev: Uuid.nullable(), next: Uuid.nullable(), index: z.number().int().positive().nullable(), total: z.number().int().nonnegative() });
export type VendorQuoteNeighbors = z.infer<typeof VendorQuoteNeighbors>;


export const VENDOR_QUOTE_LIST_MSG = {
  register: "{vendorQuote}登録",
  csv: "CSV",
  empty: "条件に合う{vendorQuote}はありません",
  emptyAll: "{vendorQuote}はまだありません",
  clear: "条件クリア",
  sum: "合計（税抜）",
  noAmount: "金額なし {n}件",
  more: "さらに表示",
  openAll: "すべて開く",
  closeAll: "すべて閉じる",
  unset: "未設定",
  issuer: "読み取り：{name}",
  readOk: "検算一致",
  readNg: "要確認",
  readFailed: "読み取れず",
  readAcked: "確認済み",
  waiting: "順番待ち",
  importBtn: "見積で取込",
  importNeedsGroup: "{project}を設定すると取込できます",
  importPick: "取込先の見積",
  groupLocked: "反映した行を解除すると変更できます",
  rejectBlocked: "反映した行を解除すると不採用にできます",
  deleteBlocked: "反映した行を解除すると削除できます",
  rejectTitle: "不採用",
  rejectBody: "「{name}」を不採用にします。",
  rejectReason: "理由（任意）",
  deleteTitle: "削除",
  deleteBody: "「{name}」を一覧から削除します。原本は保存期間のあいだ保管します。",
  rejected: "不採用にしました",
  unrejected: "不採用を解除しました",
  acked: "検算を確認済みにしました",
  unacked: "確認済みを解除しました",
  deleted: "削除しました",
  restored: "元に戻しました",
  saved: "保存しました",
  stateChanged: "状態：{from} → {to}",
  rfqLinked: "見積依頼 {no} を回答ありにしました",
  rfqUnlinked: "見積依頼 {no} との紐づけを解除しました（回答待ちに戻りました）",
  rfqCandidates: "回答待ちの見積依頼 {n}件",
  phoneNote: "紐づけ・不採用・削除はパソコンから操作します。",
  csvTooMany: "{n}件あります。CSV は 5,000件までです。期間を狭めてください",
  stale: CONFLICT_MSG.auto,
  notFound: "{vendorQuote}が見つかりません",
  userNotFound: "担当者が見つかりません",
  original: "原本",
  readResult: "AI読み取り結果",
} as const;

/** 詳細の画面（#198 の続き、docs/79 §13）の言葉。ボタンは名詞形・業務の言葉（docs/10 §5.1.2） */
export const VENDOR_QUOTE_DETAIL_MSG = {
  prev: "前へ",
  next: "次へ",
  prevTitle: "一覧の前の{vendorQuote}",
  nextTitle: "一覧の次の{vendorQuote}",
  position: "{i} ／ {n}",
  more: "その他の操作",
  details: "詳細",
  detailsOpen: "詳細表示",
  detailsClose: "詳細を閉じる",
  title: "件名",
  quoteNo: "見積番号",
  quotedOn: "見積日",
  tax: "税",
  taxEx: "税抜",
  taxIn: "税込",
  totalManual: "税抜合計",
  exTax: "金額（税抜）",
  taxAmount: "消費税",
  incTax: "金額（税込）",
  usage: "反映",
  rfq: "見積依頼",
  received: "取込日",
  read: "読み取り",
  splitter: "原本と{readResult}の境",
  widenOriginal: "原本を広く",
  widenResult: "結果を広く",
  rows: "{n}行",
  jump: "原本 P.{n} 表示",
  noLines: "行がありません",
  colNo: "No",
  colKind: "区分",
  colName: "{lineName}・{lineSpec}",
  colQty: "数量",
  colUnit: "単位",
  colPrice: "単価",
  colAmount: "金額",
  colUsed: "取込",
  used: "取込済",
  corrected: "修正済み",
  queued: "ほかのAI読み取りが終わると始まります",
  reading: "2ページで 2〜3分。ページが多いほど長くかかります",
  toManual: "手入力に切替",
  cancelRead: "取込中止",
  manual: "手入力",
  retry: "再読み取り",
  addLine: "＋ 行追加",
  unconfirm: "確定解除",
  openOriginal: "別窓で原本表示",
  unlinkRfq: "見積依頼紐づけ解除",
  reject: "不採用",
  unreject: "不採用解除",
  ack: "確認済み（検算）",
  unack: "確認済み解除",
  delete: "削除",
  saving: "保存中…",
  savedAt: "保存しました {at}",
  help: "行を押すと修正できます。見積に入れるのは「見積で取込」から、見積書の明細の行を選びます。境をドラッグ（または ← →）すると原本と結果の幅を変えられます。",
} as const;

/** 結果の表の「区分」（行の役割の言葉） */
export const VENDOR_LINE_ROLE_LABEL = {
  detail: "明細",
  subtotal: "小計",
  /** #314：見積の区分と同じ「％計算」 */
  rate: "％計算",
  discount: "値引き",
  tax: "消費税",
  total: "合計",
  heading: "見出し",
  excluded: "別途",
  adjustment: "諸経費",
  zero: "0円",
} as const;
